// Courier Escalations panel for the Customer Profile (Part 4). Read-only summary
// with repeat-failure intelligence; click a row to open the escalation dashboard.
import React, { useMemo } from 'react';
import { ShieldAlert, AlertTriangle } from 'lucide-react';
import { useCourierEscalations } from '../hooks/useCourierEscalations';
import { slaStatus, STATUS_LABELS, OWNERSHIP_LABELS, isActiveStatus } from '../logic/courierEscalation';
import { getCourierDisplayName } from '../../orders/ui/shared/courierUtils';
import { EscalationBadge } from './EscalationBadge';

const fmtSince = (t) => {
  if (!t) return '—';
  const d = Math.floor((Date.now() - t) / 86400000);
  return d <= 0 ? 'today' : `${d}d ago`;
};

const CustomerEscalationsPanel = ({ mobile }) => {
  const { forMobile, loading } = useCourierEscalations();
  const list = forMobile(mobile);

  const { open, closed, repeatCourier } = useMemo(() => {
    const open = list.filter(e => isActiveStatus(e.status));
    const closed = list.filter(e => !isActiveStatus(e.status));
    // Same courier failing repeatedly = risk signal.
    const byCourier = {};
    for (const e of list) byCourier[e.courier] = (byCourier[e.courier] || 0) + 1;
    const repeatCourier = Object.entries(byCourier).filter(([, n]) => n >= 2).map(([c]) => c);
    return { open, closed, repeatCourier };
  }, [list]);

  if (loading || list.length === 0) return null; // hide when nothing to show

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-50">
        <ShieldAlert size={18} className="text-red-600" />
        <h3 className="font-bold text-slate-800">Courier Escalations</h3>
        <span className="text-xs text-slate-400">{open.length} open · {closed.length} closed</span>
      </div>

      {repeatCourier.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-xs bg-amber-50 text-amber-700 rounded-lg px-3 py-2">
          <AlertTriangle size={14} />
          Repeat courier issues: <b>{repeatCourier.map(getCourierDisplayName).join(', ')}</b> — consider a different courier.
        </div>
      )}

      <div className="space-y-2">
        {list.slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map(e => {
          const sla = slaStatus(e);
          return (
            <div key={e.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${e.priority === 'critical' ? 'border-red-200 bg-red-50/40' : sla.breached ? 'border-red-200' : 'border-slate-100'}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <EscalationBadge escalation={e} compact />
                  <span className="text-xs text-slate-500 truncate">{e.reasonLabel}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {getCourierDisplayName(e.courier)} · {e.awb || 'no AWB'} · order {e.orderId || '—'} · {STATUS_LABELS[e.status]}
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-[11px] text-slate-500">{OWNERSHIP_LABELS[e.ownerLevel] || '—'}</div>
                <div className={`text-[11px] font-semibold ${sla.breached ? 'text-red-600' : 'text-slate-400'}`}>
                  {sla.terminal ? 'Resolved' : sla.breached ? 'SLA breached' : `since ${fmtSince(e.createdAt)}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerEscalationsPanel;
